import queryString from 'query-string'
import { extractKeyFromQuery } from './utils/url'

const connectErrorTrackerWithPlugins = pluginHub => {
  const $tracker = pluginHub.getPlugin('tracker')
  const $http = pluginHub.getHttpClient()
  const $shopify = pluginHub.getShopifyClient()
  const $logger = pluginHub.getLogger()
  const $bridge = pluginHub.getPlugin('bridge')

  /* ------------------------------------------- */

  const trackRequestError = err => {
    const { config = {}, message, statusCode = 'UNKNOWN' } = err
    const method = config.method ? config.method.toUpperCase() : 'UNKNOWN'
    const url = config.url || 'N/A'
    const errorMsg = `[${method}] ${message} (${statusCode})`

    let options

    // since axios cannot by-pass customize config to here
    // , so force to skip track collect api error here by url
    // , if track collect api failed via collect api, that will cause infinite loop
    if (url.indexOf('collect.flamingo.shop') > -1) {
      options = {
        collectDisabled: true
      }
    }

    const collectAPI = $tracker.$collectClient && $tracker.$collectClient.collectAPI

    if (url.indexOf(collectAPI) <= -1) { // skip track api itself error
      $tracker.error({
        isFatal: false,
        category: 'API',
        label: url,
        error: errorMsg,
        options
      })

      if ([408, -1].indexOf(statusCode) > -1) {
        // 超时或网络异常时记录performance
        setTimeout(() => {
          if (performance) {
            const entries = performance.getEntriesByType('resource')
            const entry = entries.find(e => e.name.includes(url) && e.startTime >= config.requestStartTime)

            if (entry) {
              const { duration, startTime, domainLookupStart, domainLookupEnd, connectStart, connectEnd, requestStart, responseStart, responseEnd } = entry

              $tracker.error({
                isFatal: false,
                category: 'API_Performance',
                label: url,
                error: JSON.stringify({
                  name: url,
                  duration,
                  startTime,
                  domainLookupStart,
                  domainLookupEnd,
                  connectStart,
                  connectEnd,
                  requestStart,
                  responseStart,
                  responseEnd
                })
              })
            }
          }
        }, 100)
      }
    }



    // use axios middleware, it is necessary to pass error for next interceptor
    throw err
  }

  /* ------------------------------------------- */

  const trackBridgeError = err => {
    // skip track for some cases, e.g. getUserInfo bridge return userIsNotLogin
    if (!err._skipTrack) {
      $tracker.error({
        isFatal: false,
        category: 'BRIDGE',
        label: err.bridgeLabel || 'N/A',
        error: err,
      })
    }
  }

  /* ------------------------------------------- */

  const trackLoggerError = error => {
    $tracker.error({
      category: 'LOGGER',
      error
    })
  }

  /* ------------------------------------------- */

  $http.useErrorInterceptor(trackRequestError)
  $shopify.useErrorInterceptor(trackRequestError)
  $logger.setTransport({
    error: trackLoggerError // replace error logger with tracker
  })

  if ($bridge) {
    $bridge.useErrorInterceptor(trackBridgeError)
  }
}

/* ------------------------------------------- */


const connectErrorTrackerWithBrowser = pluginHub => {
  const $tracker = pluginHub.getPlugin('tracker')

  /* ------------------------------------------- */

  window.addEventListener('unhandledrejection', event => {
    const error = event.reason
    if (!error || !error.message) {
      return
    }

    const message = error.config && error.config.url // if error from http
      ? `${error.message} (${error.config.url})`
      : error.message

    const stack = error.stack

    $tracker.error({
      isFatal: false,
      category: 'UNHANDLED_REJECTION',
      error: {
        message,
        stack,
      },
    })
  })

  /* ------------------------------------------- */

  window.onerror = (errorMessage, scriptURI, lineNumber, columnNumber, errorObj) => {
    const scriptInfo = scriptURI  && window.location.href.indexOf(scriptURI) === -1
      ? `${scriptURI}`
      : 'N/A'

    let errMsg = `[${scriptInfo}] ${errorMessage}`

    if (typeof lineNumber !== 'undefined') {
      errMsg += `:(${lineNumber}-${columnNumber})`
    }

    const errStack = errorObj && errorObj.stack
      ? errorObj.stack
      : 'no_stack'

    $tracker.error({
      isFatal: false,
      category: 'UNCATCHED_ERROR',
      error: errMsg,
      label: errStack
    })
  }
}

const connectTracker = pluginHub => {
  const $detector = pluginHub.getDetector()
  const $tracker = pluginHub.getPlugin('tracker')

  if ($tracker) {
    connectErrorTrackerWithPlugins(pluginHub)

    if (!$detector.isServer()) {
      connectErrorTrackerWithBrowser(pluginHub)
    }
  }
}

/* ------------------------------------------- */

const connectHttp = pluginHub => {
  const $detector = pluginHub.getDetector()
  const $http = pluginHub.getHttpClient()
  const $storage = pluginHub.getStorage()

  const $user = pluginHub.getPlugin('user')
  const $site = pluginHub.getPlugin('site')
  const $deviceKey = pluginHub.getPlugin('deviceKey')

  const clientId = $detector.getClientId()
  const clientVersion = $detector.getClientVersion()

  const isApp = $detector.isApp()

  $http.useRequestInterceptor(
    req => {
      if ($user) {
        const accessToken = $user.getAccessToken()

        if (accessToken && !req.headers['X-access-token']) {
          req.headers['X-access-token'] = accessToken
        }
      }

      if ($site) {
        const site = $site.getSiteInfo()

        req.headers['X-client-currency'] = site.currency
        req.headers['X-client-channel'] = site.id.split('_')[0].toLowerCase()
        // app的request的header带locale, 走pluginHub.options._locale; 非app不带locale, 走site.locale
        req.headers['X-client-locale'] = ($detector.isServer() && pluginHub.options && pluginHub.options._locale) || site.locale
        req.headers['X-client-region'] = site.countryCode
        req.headers['X-client-lang'] = site.locale.split('_')[0]
      }

      if(!req.headers['X-client-id']) {
        req.headers['X-client-id'] = clientId
      }
      req.headers['X-client-price-channel'] = !isApp ? 'web' : 'app'
      req.headers['X-client-version'] = clientVersion

      // FB按渠道打点
      if ($storage && typeof window !== 'undefined') {
        const sourceLocationStorage = $storage.create('source_location', { strategy: 'SESSION' })
        req.headers['X-client-source-location'] = sourceLocationStorage.getItem(window.location.href)
      }


      let utm_source = ''

      if ($detector.isServer()) {
        const url = pluginHub.options && pluginHub.options._url
        utm_source = extractKeyFromQuery(url, 'utm_source') || extractKeyFromQuery(url, '_cus')
      } else {
        utm_source = extractKeyFromQuery(window.location.href, 'utm_source') || extractKeyFromQuery(window.location.href, '_cus') || 'default'
        const sourceLocationStorage = $storage.create('utm_source', { strategy: 'SESSION' })
        sourceLocationStorage.setItem(utm_source)
      }
      if (utm_source && utm_source !== 'default') {
        req.headers['X-client-utm-source'] = utm_source
      }



      // FB按广告id打点
      if ($storage && typeof window !== 'undefined') {
        const fbContentIdStorage = $storage.create('fb_content_id', { strategy: 'SESSION' })
        const contentId = queryString.parse(window.location.search).utm_content
        req.headers['X-client-fb-content-id'] = fbContentIdStorage.getItem(contentId)
      }

      // Set Device Key
      if ($deviceKey && typeof window !== 'undefined') {
        req.headers['X-client-device-key'] = $deviceKey.getLocalDeviceKey()
      }

      return req
    }
  )
}

/* ------------------------------------------- */

const connectPlugins = pluginHub => {
  connectHttp(pluginHub)
  connectTracker(pluginHub)
}

export default connectPlugins
